import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon, Grid3X3, GripVertical, Minus, Maximize2, Move, Trash2, MoreHorizontal } from "lucide-react";
import { ContentBlock } from "@/types/case-study";
import { useImageUpload } from "@/hooks/domains/storage/useImageUpload";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import {
  CSS,
} from "@dnd-kit/utilities";

interface GalleryBlockProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
}

interface SortableImageItemProps {
  image: any;
  index: number;
  onRemove: (imageId: string) => void;
  onExpand: (imageId: string) => void;
  isExpanded: boolean;
}

const SortableImageItem: React.FC<SortableImageItemProps> = ({
  image,
  index,
  onRemove,
  onExpand,
  isExpanded,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group/image"
    >
      <img
        src={image.url}
        alt={`Gallery image ${index + 1}`}
        className={`w-full object-cover rounded-lg transition-all duration-300 ${
          isExpanded ? 'h-80' : 'h-60'
        }`}
      />
      
      {/* Image Controls */}
      <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover/image:opacity-100 transition-opacity">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="w-3 h-3 text-gray-600" />
        </button>
      </div>

      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/image:opacity-100 transition-opacity">
        {/* Expand/Collapse */}
        <button
          onClick={() => onExpand(image.id)}
          className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          <Maximize2 className="w-3 h-3 text-gray-600" />
        </button>
        
        {/* Delete */}
        <button
          onClick={() => onRemove(image.id)}
          className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-sm hover:bg-red-600"
          title="Delete image"
        >
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      </div>
    </div>
  );
};

export const GalleryBlock: React.FC<GalleryBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedImage, setExpandedImage] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const {
    uploading,
    handleImageUpload,
  } = useImageUpload({
    user,
    onImageUrlChange: (urls) => {
      const currentImages = block.content?.images || [];
      const newImages = urls.map((url, index) => ({
        id: crypto.randomUUID(),
        url,
        caption: "",
        order: currentImages.length + index,
      }));
      onUpdate({
        ...block.content,
        images: [...currentImages, ...newImages],
      });
    },
    bucketName: "case-study-images",
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e);
  };


  const handleRemoveImage = (imageId: string) => {
    const images = (block.content?.images || []).filter(img => img.id !== imageId);
    onUpdate({
      ...block.content,
      images,
    });
  };


  const handleExpandImage = (imageId: string) => {
    setExpandedImage(expandedImage === imageId ? null : imageId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const images = block.content?.images || [];
      const oldIndex = images.findIndex((img: any) => img.id === active.id);
      const newIndex = images.findIndex((img: any) => img.id === over?.id);

      const newImages = arrayMove(images, oldIndex, newIndex);
      // Update order values
      newImages.forEach((img: any, index: number) => {
        img.order = index;
      });

      onUpdate({
        ...block.content,
        images: newImages,
      });
    }
  };

  const images = block.content?.images || [];

  if (!isActive) {
    // Preview Mode - Show gallery in card format
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {images.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {images.map((image, index) => (
              <div key={image.id} className="relative group/image">
                <img
                  src={image.url}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-72 object-cover rounded-lg shadow-sm"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Grid3X3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Image Gallery</p>
          </div>
        )}
      </div>
    );
  }

  // Edit Mode - Show full editor interface
  return (
    <div className={`group relative ${isActive ? '' : ''}`}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Content Area */}
        <div className="p-6">
          {/* Upload Area - Always at the top */}
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2 font-medium">Upload files</p>
              <p className="text-sm text-gray-500">Click to browse or drag here</p>
            </div>
          </div>

          {/* Images Grid */}
          {images.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={images.map((img: any) => img.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 gap-4">
                  {images.map((image, index) => (
                    <SortableImageItem
                      key={image.id}
                      image={image}
                      index={index}
                      onRemove={handleRemoveImage}
                      onExpand={handleExpandImage}
                      isExpanded={expandedImage === image.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {isRemovable && onRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg"
            title="Delete this content block"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

